import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import Input from '../ui/Input';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import usePersistedCollapse from '../../hooks/usePersistedCollapse';

// Save/load controls for the whole PrintableTemplate document (name, which
// saved template is loaded, save/discard/new/delete). No product picker —
// templates aren't tied to a product.
export default function TemplatePanel({
  templates,
  templatesLoading,
  selectedId,
  onSelect,
  name,
  onNameChange,
  nameError,
  onSave,
  onSaveAs,
  onDiscard,
  onNew,
  onDelete,
  saving,
  isDirty,
}) {
  const [collapsed, setCollapsed] = usePersistedCollapse('template');

  return (
    <div className="ss-print__panel">
      <div className="ss-print__panel-header">
        <h3 className="ss-print__panel-title">Template</h3>
        <button
          type="button"
          className="ss-print__lock-toggle"
          onClick={() => setCollapsed(!collapsed)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand template panel' : 'Collapse template panel'}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
        </button>
      </div>

      {!collapsed && (
        <>
          <div className="ss-field">
            <label htmlFor="print-template-select" className="ss-label">Load template</label>
            <select
              id="print-template-select"
              className="ss-input"
              value={selectedId || ''}
              onChange={(e) => onSelect(e.target.value)}
              disabled={templatesLoading || saving}
            >
              <option value="">New template…</option>
              {templates.map((t) => (
                <option key={t._id} value={t._id}>{t.name}</option>
              ))}
            </select>
          </div>

          <Input
            label="Name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. 2x3 shelf labels"
            disabled={saving}
            error={nameError}
          />

          <div className="ss-form__actions">
            <div className="ss-form__row">
              {/* Creating a brand-new template (no selectedId): disabled until
                  a name is typed in. Saving an already-loaded template:
                  disabled until there are unsaved changes (isDirty), same as
                  Discard/Delete/New. */}
              <Button size="sm" onClick={onSave} disabled={saving || (selectedId ? !isDirty : !name.trim())}>
                {saving ? <Spinner label="Saving" /> : selectedId ? 'Save' : 'Create'}
              </Button>
              {/* Only meaningful once a template exists to copy from — a
                  brand-new unsaved template already saves as a copy via
                  Create above. */}
              <Button size="sm" variant="ghost" onClick={onSaveAs} disabled={saving || !selectedId}>
                Save as…
              </Button>
            </div>
            <div className="ss-form__row">
              {/* Reverts to the last save (a loaded template) or back to
                  blank (a new, never-saved template) — either way, only
                  meaningful once there's something to revert. */}
              <Button size="sm" variant="ghost" onClick={onDiscard} disabled={saving || !isDirty}>
                Revert
              </Button>
              {/* Switches to a blank new template: always available once a
                  saved template is loaded (nothing to lose), or once a new
                  template has been dirtied (something to reset). */}
              <Button size="sm" variant="ghost" onClick={onNew} disabled={saving || (!selectedId && !isDirty)}>New</Button>
            </div>
            <Button size="sm" variant="danger" onClick={onDelete} disabled={saving || !selectedId}>Delete</Button>
          </div>
        </>
      )}
    </div>
  );
}
